import { DatePicker, TimePicker } from 'components/pickers/DateTimePicker'
import { selectWarehouses } from 'containers/Warehouses/warehousesSlice'
import usePreviousAccess from '../hooks/usePreviousAccess'
import PhoneInput from 'components/inputs/PhoneInput'
import { AccessType } from '../hooks/useAccessParams'
import useSendingForm from '../hooks/useSendingForm'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import BackBtn from 'components/buttons/BackBtn'
import { DEFAULT_PREFIX } from 'utils/phone'
import Select from 'components/inputs/Select'
import { dateToYMDformat } from 'utils/date'
import Input from 'components/inputs/Input'
import { useReduxSelector } from 'store'
import React, { useState } from 'react'

interface NewAccessProps {
  isPopup?: boolean
  warehouseId: string
  permissionId?: string
  action: AccessType
  callback: basic.BasicFunc
}
export interface Form {
  warehouseIds: string[],
  name: string
  prefix: string
  phone: string
  date?: string
  time?: string
  disabled?: boolean
  resetOneTime?: boolean
  functions?: string[]
}

const options = {
  'time-access': [{
    value: 'entry',
    desc: 'accessToTheWarehouse'
  }, {
    value: 'invoice',
    desc: 'invoiceManagement'
  }, {
    value: 'payment',
    desc: 'makingPayments'
  }, {
    value: 'events',
    desc: 'eventPreview'
  }, {
    value: 'contract',
    desc: 'resignationContractExtension'
  }],
  'permanent-access': [{
    value: 'entry',
    desc: 'accessToTheWarehouse'
  }, {
    value: 'invoice',
    desc: 'invoiceManagement'
  }, {
    value: 'payment',
    desc: 'makingPayments'
  }, {
    value: 'share',
    desc: `sharingMAG.${process.env.REACT_APP_TYPE}`
  }, {
    value: 'events',
    desc: 'eventPreview'
  }, {
    value: 'contract',
    desc: 'resignationContractExtension'
  }]
}

const type = process.env.REACT_APP_TYPE

const NewAccess: React.FC<NewAccessProps> = ({ isPopup, warehouseId, permissionId, action, callback }) => {
  const warehouses = useReduxSelector(selectWarehouses)
  const [form, setForm] = useState<Form>({
    warehouseIds: warehouseId ? [warehouseId] : [],
    name: '',
    prefix: DEFAULT_PREFIX,
    phone: ''
  })

  const date = new Date()
  const { isSending, errors, validateForm, remove } = useSendingForm(form, action, callback, permissionId)
  const { previousPermission } = usePreviousAccess(setForm, permissionId)

  const { t } = useTranslation('warehouse')
  const updateForm = <T extends keyof Form>(key: T, value: Form[T]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className='menu__section section__newAccess'>
      <div className='title'>
        <div>{t(action)}</div>
        <div>{t('completeTheData')}</div>
      </div>

      <div className='section__newAccess__main'>
        <div>
          <Input
            name='name'
            label={t('fullName')}
            placeholder={t('enterYourFullname')}
            disabled={isSending}
            warning={errors.name}
            value={form.name}
            changeHandler={e => {
              updateForm('name', e.target.value)
            }}
          />
          <PhoneInput
            name='phone'
            label={t('phoneNumber')}
            placeholder={t('enterYourPhoneNumber')}
            disabled={!!permissionId || isSending}
            warning={errors.phone}
            value={form.phone}
            prefix={form.prefix}
            setValue={value => setForm(prev => ({ ...prev, ...value }))}
          />
          <Select
            multiple
            secondaryTheme
            label={t(type === 'mm' ? 'rental' : 'warehouse')}
            name='warehouseIds'
            disabled={!!permissionId || isSending}
            value={form.warehouseIds}
            warning={errors.warehouseIds}
            options={warehouses.map(({ typeName, name, objectId, placeShortAddress }) => ({ name: `${typeName} nr. ${name} (${placeShortAddress})`, value: objectId }))}
            setValue={v => {
              updateForm('warehouseIds', v)
            }}
          />
          {action === 'time-access' && <div>
            <p>{t('untilWhenDoYouWantToProvideAccess')}</p>
            <div className='center --gap1'>
              <DatePicker
                isPopup={isPopup}
                name='access-date'
                value={form.date}
                disabled={isSending}
                warning={errors.date}
                minDate={dateToYMDformat(date)}
                setValue={(date) => {
                  updateForm('date', date)
                }}
              />
              <TimePicker
                isPopup={isPopup}
                name='access-time'
                value={form.time}
                defaultTime={date.toLocaleTimeString()}
                minTime={form.date === dateToYMDformat(date)
                  ? date.toLocaleTimeString()
                  : undefined}
                disabled={isSending}
                warning={errors.time}
                setValue={(time) => {
                  updateForm('time', time)
                }}
              />
            </div>
          </div>}

          {action === 'one-time-access'
            ? <div>
              <p>{t('importantInformation')}</p>
              <div className='section__newAccess__info'>
                {t('byGrantingSingleAccessTheUserWillReceiveAnEmailOrTextMessageWithACodeToOpenTheGateAndWarehouseThisCodeWillOnlyWorkOnce')}
              </div>
            </div> : <div>
              <p>{t('whichFeaturesWouldYouLikeToMakeAvailable')}</p>
              <div className='section__newAccess__options'>
                {options[action].map(({ desc, value }) => {
                  const isSelected = form.functions?.includes(value)
                  return <div
                    key={value}
                    className={`section__newAccess__option${isSelected ? ' --selected' : ''}`}
                    onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        functions: isSelected
                          ? prev.functions!.filter(f => f !== value)
                          : [...(prev.functions || []), value]
                      }))
                    }}>
                    <div className='--secFont'>{t(value)}</div>
                    <div>{t(desc)}</div>
                  </div>
                })}
              </div>
            </div>}

          {previousPermission && <div>
            <p>{t('additionalInformation')}</p>
            {action === 'one-time-access' && !previousPermission.isActive
              ? <Checkbox
                name='resetOneTime'
                label={t('resetOne-timeAccess')}
                checked={form.resetOneTime || false}
                changeHandler={() => {
                  updateForm('resetOneTime', !form.resetOneTime)
                }} />
              : <Checkbox
                name='disabled'
                label={t('blockedTemporarilyDisabledAccess')}
                checked={form.disabled || false}
                changeHandler={() => {
                  updateForm('disabled', !form.disabled)
                }} />}
          </div>}
        </div>

        {previousPermission?.hasOtherPermissions && <div className='section__newAccess__text'>
          {previousPermission.otherPermissions.length === 1
            ? t('youHave1MoreSharedWarehouses')
            : `${t('youStillHave')} ${previousPermission.otherPermissions.length} ${t(previousPermission.otherPermissions.length < 5 ? 'sharedWarehouses<5' : 'sharedWarehouses>=5')}`}
        </div>}

        <div className='section__newAccess__buttons'>
          <ArrowBtn
            enlarged
            text={t(permissionId ? 'update' : 'save')}
            isLoading={isSending}
            action={validateForm}
          />
          {permissionId && <BackBtn
            text={t('delete')}
            action={remove}
          />}
        </div>
      </div>
    </div>
  )
}

export default NewAccess